import { getSiteMetadata } from "./config";
import { QuizSlideState } from "./lib/quiz-lib";
import { CheckoutAnswers, ServerUser, WebSession } from "./services/coreapi";

export type ConversionDetails = {
  paymentType?: string | null;
  value?: number;
  currency?: string | null;
  productID?: string | null;
  orderID?: string | null;
};

export type BrowserLocalStorageState = {
  user?: ServerUser;
  couponID?: string | null;
  purchaseTrackedAt?: string;
  devMode?: boolean;
  hasFinishedOnboarding?: boolean;
  conversionDetails?: ConversionDetails;
  onboardingVersion?: string;
  hasReachedCheckout?: boolean;
  acceptedPromotionID?: string;
  downselling?: {
    currentOfferID?: string;
    hasFinished?: boolean;
  };
  promoCode?: string | null;
  purchasedUpsells?: Array<{ productID: string; purchasedAt: string }>;
  sessionId?: string;
  webSession?: WebSession;
  quizState?: QuizSlideState;
  /**
   * Id of the funnel that owns the persisted `quizState`. A funnel only restores
   * `quizState` when this matches its own id, preventing cross-funnel answer
   * bleed (slide IDs overlap across funnels). See `quizFunnelIds.ts`.
   */
  quizStateFunnelId?: string;
  /** @deprecated Old first-time login token. Wiped on every page load; never written. */
  authToken?: string;
  /** One-time code from the newest pay reply. "Open App" hands it to the app as `#code=`. */
  handoffCode?: string | null;
  /** Checkout session token. Sent only as the `x-nt-checkout` header, never in a URL or a log. */
  checkoutToken?: string | null;
  /** The email this checkout will pay with. Cleared whenever the token changes. */
  checkoutEmail?: string | null;
  /** The checkout's answers, from GET /checkout/me. Read only when there is no `user`. */
  checkoutAnswers?: CheckoutAnswers | null;
};

function getKey() {
  return getSiteMetadata().appName;
}

function getStorage() {
  if (typeof window !== "undefined") {
    return window.localStorage;
  }
}

const EMPTY: BrowserLocalStorageState = {};

// In-memory copy of the persisted state. Guarantees `set` survives storage
// failures (Safari private mode / quota) within the pageload, and gives `get`
// referential stability so React subscribers can bail out on no-op syncs.
let cache: BrowserLocalStorageState | null = null;

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

/**
 * Subscribe to writes made through this module (`set`/`clearAll`), from any
 * layer — React or plain services. Returns an unsubscribe function. This keeps
 * a single write path: services write here directly and React mirrors follow,
 * instead of every writer having to remember to sync React state too.
 */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readStorage(): BrowserLocalStorageState {
  try {
    const item = getStorage()?.getItem(getKey());
    if (!item) {
      return EMPTY;
    }

    return JSON.parse(item);
  } catch (err) {
    console.error("Failed to parse data from local storage");
    // Self-heal without notifying: `get` runs during React renders, and
    // notifying mid-render would trigger setState-in-render warnings.
    try {
      getStorage()?.removeItem(getKey());
    } catch {
      // ignore
    }
    return EMPTY;
  }
}

function set(data: BrowserLocalStorageState) {
  cache = data;
  try {
    getStorage()?.setItem(getKey(), JSON.stringify(data));
  } catch (err) {
    // Safari private mode / quota exceeded — keep the in-memory state working
    // instead of letting the write throw into React.
    console.error("Failed to write data to local storage", err);
  }
  notify();
}

function get(): BrowserLocalStorageState {
  return (cache ??= readStorage());
}

function clearAll() {
  cache = EMPTY;
  try {
    // Even the `window.localStorage` accessor can throw when site data is
    // blocked (webviews, "block all cookies" settings).
    getStorage()?.removeItem(getKey());
  } catch (err) {
    console.error("Failed to clear local storage", err);
  }
  notify();
}

// Cross-tab sync: a write in another tab invalidates the cache and re-renders
// subscribers (the `storage` event only fires in the tabs that did NOT write).
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    try {
      if (event.key === getKey()) {
        cache = null;
        notify();
      }
    } catch {
      // site metadata not registered yet — nothing to sync
    }
  });
}

export const browserLocalStorage = {
  clearAll,
  set,
  get,
  subscribe,
};
